import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { srcSetFor } from "@/lib/images";

/** True when the viewport matches the query (default: lg and up). Films are desktop-only. */
export const useMedia = (query = "(min-width: 1024px)") => {
  const [m, setM] = useState(() => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const on = (e) => setM(e.matches);
    setM(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on));
  }, [query]);
  return m;
};

/**
 * Keeps a hero <video> honest: it only plays while on screen, pauses when the tab is hidden, and
 * reports when the first frames are actually decodable so we can crossfade from the still without
 * a black flash.
 */
export const useHeroVideo = (enabled) => {
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const v = videoRef.current;
    if (!enabled || !v) return undefined;
    const tryPlay = () => v.play().catch(() => {});
    const onPlaying = () => setReady(true);
    v.addEventListener("playing", onPlaying);
    const io = new IntersectionObserver(([e]) => (e.isIntersecting ? tryPlay() : v.pause()), { threshold: 0.05 });
    io.observe(v);
    const onVis = () => (document.hidden ? v.pause() : tryPlay());
    document.addEventListener("visibilitychange", onVis);
    tryPlay();
    return () => {
      v.removeEventListener("playing", onPlaying);
      document.removeEventListener("visibilitychange", onVis);
      io.disconnect();
    };
  }, [enabled]);
  return { videoRef, ready };
};

/**
 * Still photo shown instantly, with an optional muted film that fades in on top once it plays.
 * `video` = { mp41080, mp4720, webm720 }. `enabled` decides whether the film is used at all
 * (callers pass false on phones and for prefers-reduced-motion so the still is kept).
 * Fills its parent (absolute inset-0). Slow settle-zoom on load.
 */
export const HeroMedia = ({ image, alt = "", position = "50% 50%", mobilePosition, video, enabled = false, label, testid = "hero-media" }) => {
  const reduce = useReducedMotion();
  const { videoRef, ready } = useHeroVideo(enabled && !!video);
  return (
    <motion.div
      initial={reduce ? false : { scale: 1.06 }}
      animate={{ scale: 1 }}
      transition={{ duration: 7, ease: [0.16, 1, 0.3, 1] }}
      className="absolute inset-0 h-full w-full origin-center"
      data-testid={testid}
      data-video={enabled && video ? (ready ? "playing" : "loading") : "off"}
    >
      <img
        src={image}
        srcSet={srcSetFor(image)}
        sizes="100vw"
        alt={alt}
        className="h-full w-full object-cover [object-position:var(--pos-m)] lg:[object-position:var(--pos)]"
        style={{ "--pos-m": mobilePosition || position, "--pos": position }}
        fetchPriority="high"
        decoding="async"
        data-testid={`${testid}-image`}
      />
      {enabled && video && (
        <video
          ref={videoRef}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity [transition-duration:1600ms] ease-out ${ready ? "opacity-100" : "opacity-0"}`}
          style={{ objectPosition: position }}
          muted
          loop
          playsInline
          autoPlay
          preload="auto"
          disablePictureInPicture
          poster={image}
          aria-label={label}
          data-testid={`${testid}-video`}
        >
          {video.mp41080 && <source src={video.mp41080} type="video/mp4" media="(min-width: 1440px)" />}
          {video.mp4720 && <source src={video.mp4720} type="video/mp4" />}
          {video.webm720 && <source src={video.webm720} type="video/webm" />}
        </video>
      )}
    </motion.div>
  );
};
